import { motion } from "framer-motion";
import {
  Zap,
  Car,
  Forklift,
  BatteryCharging,
  CircuitBoard,
  Cable,
  Cog,
  Radio,
  Waves,
  SlidersVertical,
  Cpu,
  Settings2,
  ArrowUpRight,
} from "lucide-react";
import { mailto, scrollToId } from "./nav";

const DIRECTIONS = [
  {
    icon: Zap,
    title: "Маломощные электродвигатели",
    desc: "Поставка маломощных электродвигателей и сопутствующих компонентов для промышленных и специализированных применений.",
    tags: ["Двигатели", "Комплектующие"],
    subject: "маломощные электродвигатели",
  },
  {
    icon: Car,
    title: "Электротранспорт",
    desc: "Электромеханические компоненты для пассажирских электромобилей, электромотоциклов и малых электросудов.",
    tags: ["Автомобили", "Мотоциклы", "Электросуда"],
    subject: "электротранспорт",
  },
  {
    icon: Forklift,
    title: "Складская техника",
    desc: "Электродвигатели и комплектующие для погрузчиков, штабелёров, электротележек и робототехнических манипуляторов.",
    tags: ["Погрузчики", "Штабелёры", "Тележки", "Манипуляторы"],
    subject: "складская техника",
  },
  {
    icon: BatteryCharging,
    title: "Гибридные силовые установки",
    desc: "Интеграция электромеханических решений в гибридные силовые системы электротранспорта и промышленных установок.",
    tags: ["Гибрид", "Интеграция"],
    subject: "гибридные силовые установки",
  },
  {
    icon: CircuitBoard,
    title: "Силовая электроника",
    desc: "Партнёрство с разработчиками силовой электроники: комплектные решения силовой электроники для промышленных задач.",
    tags: ["Модули", "Комплектные решения"],
    subject: "силовая электроника",
  },
  {
    icon: Waves,
    title: "Инверторы",
    desc: "Поставка инверторов для электропривода, электротранспорта и гибридных силовых систем.",
    tags: ["Электропривод", "Гибридные системы"],
    subject: "инверторы",
  },
  {
    icon: SlidersVertical,
    title: "Частотные преобразователи",
    desc: "Частотные преобразователи для управления электродвигателями в промышленных установках и складской технике.",
    tags: ["ЧП", "Управление приводом"],
    subject: "частотные преобразователи",
  },
  {
    icon: Cpu,
    title: "Промышленная автоматизация",
    desc: "Комплектующие для систем промышленной автоматизации: контроллеры движения, датчики, интерфейсные модули.",
    tags: ["Автоматизация", "Управление"],
    subject: "промышленная автоматизация",
  },
  {
    icon: Settings2,
    title: "Электроприводные системы",
    desc: "Компоненты электроприводных систем для промышленной и складской техники, электротранспорта и автоматизации.",
    tags: ["Электропривод", "Промышленность"],
    subject: "электроприводные системы",
  },
];

const CAPABILITIES = [
  {
    icon: Cog,
    title: "Подбор технических решений",
    text: "Комплектуем позиции под задачу заказчика и параметры оборудования.",
  },
  {
    icon: Cable,
    title: "Работа со специализированными рынками",
    text: "Работаем с промышленными и техническими сегментами: электротранспорт, склад, автоматизация, электропривод.",
  },
  {
    icon: Radio,
    title: "Партнёрская сеть",
    text: "Взаимодействуем с производителями и разработчиками электромеханики и силовой электроники.",
  },
];

export default function Motors() {
  return (
    <section
      id="motors"
      data-testid="motors-section"
      className="section relative bg-[#0A192F] text-white overflow-hidden"
    >
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div
        className="absolute -top-40 -left-40 w-[500px] h-[500px] opacity-20 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,0,0.5) 0%, transparent 70%)",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-16 lg:mb-24">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-6 !text-[#FF6B00]">Электромеханика · Ключевое направление</div>
            <h2 className="font-display text-white text-3xl sm:text-4xl xl:text-5xl font-semibold leading-[1.15] tracking-tight">
              Электродвигатели и&nbsp;электромеханика
            </h2>
            <p className="mt-6 text-white/75 text-base lg:text-lg leading-relaxed">
              Отдельное направление B2B-поставок: маломощные электродвигатели и сопутствующая
              электромеханика для электротранспорта, складской техники, промышленной автоматизации
              и электроприводных систем. Работаем с инверторами, частотными преобразователями
              и силовой электроникой.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {["Электротранспорт", "Склад", "Гибридные системы", "Силовая электроника", "Автоматизация", "Электропривод"].map(
                (t) => (
                  <span
                    key={t}
                    className="text-xs uppercase tracking-[0.14em] font-medium text-white/70 border border-white/20 px-3 py-1.5"
                  >
                    {t}
                  </span>
                )
              )}
            </div>

            <a
              href={mailto("Запрос: маломощные электродвигатели и электромеханика")}
              data-testid="motors-main-cta"
              className="mt-10 inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#E66000] text-white px-8 py-4 font-medium transition-colors"
            >
              Запросить предложение
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <div className="lg:col-span-6">
            <div className="relative overflow-hidden">
              <img
                src="https://images.pexels.com/photos/3846205/pexels-photo-3846205.jpeg"
                alt="Зарядка электротранспорта — электромеханические решения"
                className="w-full h-[380px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/30 to-transparent" />
              <div className="absolute top-6 left-6 glass-card-dark px-4 py-2 text-white text-xs uppercase tracking-[0.2em]">
                Электротранспорт · склад · автоматизация
              </div>
            </div>
          </div>
        </div>

        {/* Directions grid */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FF6B00] font-semibold mb-2">
            Ключевые направления
          </div>
          <h3 className="font-display text-white text-2xl lg:text-3xl font-semibold">
            Области применения
          </h3>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="motors-directions"
        >
          {DIRECTIONS.map((d, i) => {
            const Icon = d.icon;
            return (
              <motion.article
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.05 }}
                data-testid={`motors-direction-${i}`}
                className="group relative bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-[#FF6B00]/40 p-7 flex flex-col transition-all"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 bg-white/10 group-hover:bg-[#FF6B00] flex items-center justify-center transition-colors">
                    <Icon className="w-5 h-5 text-[#FF6B00] group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-xs tabular-nums font-semibold tracking-widest text-white/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h4 className="font-display text-white text-lg font-semibold leading-snug">
                  {d.title}
                </h4>
                <p className="mt-3 text-white/70 text-sm leading-relaxed flex-1">
                  {d.desc}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {d.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] uppercase tracking-widest font-medium text-white/60 border border-white/15 px-2.5 py-1"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <a
                  href={mailto(`Запрос: ${d.subject}`)}
                  data-testid={`motors-cta-${i}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-white text-sm font-medium hover:text-[#FF6B00] transition-colors self-start"
                >
                  Запросить предложение
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </motion.article>
            );
          })}
        </div>

        {/* Capabilities row */}
        <div className="mt-20 lg:mt-28 grid md:grid-cols-3 gap-5">
          {CAPABILITIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`motors-capability-${i}`}
                className="bg-white/[0.04] border-t-2 border-[#FF6B00]/60 p-6"
              >
                <Icon className="w-6 h-6 text-[#FF6B00] mb-4" />
                <div className="font-display text-white font-semibold text-base leading-snug">
                  {c.title}
                </div>
                <div className="mt-2 text-sm text-white/70 leading-relaxed">
                  {c.text}
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 lg:mt-20 border border-white/10 p-8 lg:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-display text-white text-xl lg:text-2xl font-semibold">
              Нужно комплектное решение?
            </div>
            <div className="mt-2 text-white/70">
              Отправьте техническое задание — подберём электродвигатели и сопутствующие
              компоненты под ваш проект.
            </div>
          </div>
          <button
            onClick={() => scrollToId("request")}
            data-testid="motors-general-cta"
            className="shrink-0 inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#E66000] text-white px-8 py-4 font-medium transition-colors"
          >
            Отправить запрос
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
